import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Skeleton, SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useView } from '../../../lib/access/useView';
import { projectDataClient } from '../../../lib/connect/clients';
import { CustomerThemeEvidence } from '../CustomerThemeEvidence';
import { Choice, TextField } from '../PastPerformance/Fields';
import { usePursuitCapabilities } from '../record/usePursuitCapabilities';
import { BlackHatPanel } from './BlackHatPanel';
import { outputsKey, snapshotsKey, useCaptureOutputs, useOutputSnapshots } from './hooks';
import { MaterialEditor } from './MaterialEditor';
import { OutputLifecycle } from './OutputLifecycle';
import { accessDenied, dateLabel, ownerDestination, readinessWhy, templateLabel, templates } from './outputPresentationModel';
import { SourceContent } from './SourceContent';
import { WinThemesPanel } from './WinThemesPanel';
export function OutputsWorkspace({ captureProjectId, zouProjectId, active = true, showTitle = true }) {
    const location = useLocation();
    const navigate = useNavigate();
    const cache = useQueryClient();
    const params = new URLSearchParams(location.search);
    const templateId = params.get('outputTemplate') || 'capture-review-v1';
    const sectionKey = params.get('outputSection') || '';
    const snapshotId = params.get('outputSnapshot') || '';
    const query = useCaptureOutputs(captureProjectId, templateId);
    const history = useOutputSnapshots(captureProjectId);
    const wasActive = useRef(active);
    useEffect(()=>{
        if (active && !wasActive.current) {
            void cache.invalidateQueries({
                queryKey: outputsKey(captureProjectId)
            });
            void cache.invalidateQueries({
                queryKey: snapshotsKey(captureProjectId)
            });
        }
        wasActive.current = active;
    }, [
        active,
        cache,
        captureProjectId
    ]);
    // GRW-30: the one capability gate (header capabilities in the record shell,
    // useView capture_project update outside it).
    const { canEdit } = usePursuitCapabilities({
        captureProjectId,
        zouProjectId
    });
    const writeAccess = useView({
        kind: 'project_write',
        id: captureProjectId,
        objectType: 'delivery.programs',
        objectId: zouProjectId
    }, {
        action: 'project'
    });
    const canWrite = canEdit && writeAccess.can('project');
    const [refused, setRefused] = useState(false);
    const [purpose, setPurpose] = useState('');
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const draftSessions = useRef(new Map());
    const [saveError, setSaveError] = useState('');
    const [saveMessage, setSaveMessage] = useState('');
    const selectedSnapshot = history.data?.snapshots.find((snapshot)=>snapshot.id === snapshotId);
    const projection = snapshotId ? selectedSnapshot?.projection : query.data?.projection;
    const selected = projection?.sections.find((section)=>section.key === sectionKey) ?? (!sectionKey ? projection?.sections[0] : undefined);
    const denied = refused || query.isError && accessDenied(query.error) || history.isError && accessDenied(history.error);
    function update(values) {
        const next = new URLSearchParams(location.search);
        next.set('tab', 'outputs');
        next.delete('outputOwner');
        for (const [key, value] of Object.entries(values)){
            if (value) next.set(key, value);
            else next.delete(key);
        }
        navigate({
            pathname: location.pathname,
            search: next.toString()
        });
    }
    function openOwner(section) {
        const target = ownerDestination(section);
        if (!target) {
            document.querySelector(`[data-output-editor="${section.key}"] textarea`)?.focus();
            return;
        }
        const next = new URLSearchParams(location.search);
        next.set('tab', target.tab);
        next.set('outputSection', section.key);
        next.delete('overviewKind');
        next.delete('overviewId');
        if (target.anchor) next.set('outputOwner', target.anchor);
        else next.delete('outputOwner');
        navigate({
            pathname: location.pathname,
            search: next.toString()
        });
    }
    async function saveSnapshot() {
        if (inFlight.current || !purpose.trim() || !canWrite) return;
        inFlight.current = true;
        setBusy(true);
        setSaveError('');
        setSaveMessage('');
        try {
            const result = await projectDataClient.saveCaptureOutputSnapshot({
                captureProjectId,
                templateId,
                purpose: purpose.trim()
            });
            if (!result.snapshot) throw new Error('No snapshot returned');
            setSaveMessage(`Saved copy version ${result.snapshot.version}. Review it in History.`);
            setPurpose('');
            await Promise.all([
                cache.invalidateQueries({
                    queryKey: snapshotsKey(captureProjectId)
                }),
                cache.invalidateQueries({
                    queryKey: outputsKey(captureProjectId)
                })
            ]);
        } catch (cause) {
            const code = ConnectError.from(cause).code;
            if (code === Code.PermissionDenied || code === Code.NotFound || code === Code.Unauthenticated) setRefused(true);
            else setSaveError('The copy could not be saved. Your purpose is retained. Retry when the connection is available.');
        } finally{
            setBusy(false);
            inFlight.current = false;
        }
    }
    if (denied) return /*#__PURE__*/ _jsx("div", {
        role: "alert",
        "data-testid": Testids.captureOutputs.error,
        className: "p-4",
        children: "Outputs access is unavailable. Read and sensitive Pursuit access are required. No output content is displayed."
    });
    if (query.isPending || history.isPending) return /*#__PURE__*/ _jsxs("output", {
        "data-testid": Testids.captureOutputs.loading,
        className: "space-y-3 p-4",
        children: [
            "Loading Outputs…",
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-32 w-full"
            })
        ]
    });
    if (query.isError) return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        "data-testid": Testids.captureOutputs.error,
        children: [
            "Outputs could not be loaded.",
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureOutputs.retry,
                onClick: ()=>query.refetch(),
                children: "Retry Outputs"
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.captureOutputs.root,
        className: "space-y-5 py-4",
        children: [
            showTitle && /*#__PURE__*/ _jsx("h2", {
                className: "text-xl font-semibold",
                children: "Outputs"
            }),
            /*#__PURE__*/ _jsx(OutputLifecycle, {
                captureProjectId: captureProjectId,
                templateId: templateId,
                canWrite: canWrite,
                active: active,
                zouProjectId: zouProjectId,
                sourceUpdatedAt: query.dataUpdatedAt,
                onDenied: ()=>setRefused(true)
            }, `${captureProjectId}:${templateId}`),
            !snapshotId && /*#__PURE__*/ _jsx(CustomerThemeEvidence, {
                receiver: "outputs",
                objectId: captureProjectId,
                readOnly: !canWrite
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid gap-3",
                children: [
                    /*#__PURE__*/ _jsx(WinThemesPanel, {
                        pursuitId: captureProjectId,
                        canWrite: canWrite,
                        onOpenCustomerThemes: ()=>openOwner({
                                key: 'customer_themes',
                                owner: 'Customer Themes'
                            })
                    }),
                    /*#__PURE__*/ _jsx(BlackHatPanel, {
                        pursuitId: captureProjectId,
                        canWrite: canWrite,
                        active: active
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>update({
                                        outputTemplate: 'black-hat-report-v1',
                                        outputVersion: null,
                                        outputCompare: null,
                                        outputSnapshot: null,
                                        outputSection: null
                                    }),
                                children: "Black Hat report"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>update({
                                        outputTemplate: 'executive-brief-v1',
                                        outputVersion: null,
                                        outputCompare: null,
                                        outputSnapshot: null,
                                        outputSection: null
                                    }),
                                children: "Executive Brief"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-3 rounded-md border p-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-lg font-semibold",
                        children: "Capture deck · sections and sources"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Internal content coverage is ready areas out of every area in the selected template. It does not mean it is approved to share outside. Saved copies keep the source content and readiness; rendered deck files are unavailable."
                    }),
                    !snapshotId && /*#__PURE__*/ _jsx(Choice, {
                        label: "Review template",
                        testId: Testids.captureOutputs.template,
                        value: templateId,
                        options: templates,
                        onChange: (value)=>update({
                                outputTemplate: value,
                                outputSection: null,
                                outputVersion: null,
                                outputCompare: null
                            })
                    }),
                    snapshotId ? /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsxs("h4", {
                                className: "font-semibold",
                                children: [
                                    "Saved copy · ",
                                    selectedSnapshot?.purpose || 'Unavailable'
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Version ",
                                    selectedSnapshot?.version,
                                    " · ",
                                    dateLabel(selectedSnapshot?.createdAt ?? ''),
                                    " ·",
                                    ' ',
                                    templateLabel(projection?.templateId ?? '')
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: "This saved copy does not change. Open Source visits the current owning workspace."
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                "data-testid": Testids.captureOutputs.live,
                                onClick: ()=>update({
                                        outputSnapshot: null,
                                        outputTemplate: projection?.templateId ?? templateId
                                    }),
                                children: "Return to the live view"
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Live view · as of ",
                            dateLabel(projection?.asOf ?? '')
                        ]
                    }),
                    !projection ? /*#__PURE__*/ _jsx("output", {
                        children: snapshotId ? 'This saved copy is unavailable or you no longer have access. Return to the live view.' : 'Nothing is available to show for this pursuit. Retry Outputs.'
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs("p", {
                                "data-testid": Testids.captureOutputs.readiness,
                                className: "font-semibold",
                                children: [
                                    projection.readySections,
                                    " of ",
                                    projection.totalSections,
                                    " areas ready ·",
                                    ' ',
                                    templateLabel(projection.templateId)
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-sm",
                                children: [
                                    "Needs attention:",
                                    ' ',
                                    projection.sections.filter((section)=>section.readiness !== 'ready').map((section)=>section.title).join('; ') || 'None'
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid gap-4 2xl:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsx("ul", {
                                        "aria-label": "Deck areas",
                                        className: "max-h-96 space-y-2 overflow-y-auto",
                                        children: projection.sections.map((section)=>/*#__PURE__*/ _jsxs("li", {
                                                "data-testid": Testids.captureOutputs.section,
                                                "data-section-key": section.key,
                                                "data-readiness": section.readiness,
                                                className: `rounded-md border p-3 ${selected?.key === section.key ? 'border-primary bg-muted/30' : ''}`,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        "data-testid": Testids.captureOutputs.sectionSelect,
                                                        "aria-pressed": selected?.key === section.key,
                                                        className: "h-auto whitespace-normal text-left",
                                                        onClick: ()=>update({
                                                                outputSection: section.key
                                                            }),
                                                        children: section.title
                                                    }),
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                                                children: section.readiness
                                                            }),
                                                            " · ",
                                                            section.satisfied,
                                                            " /",
                                                            ' ',
                                                            section.required,
                                                            " criteria"
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-sm",
                                                        children: section.owner
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-sm text-muted-foreground",
                                                        children: readinessWhy(section)
                                                    })
                                                ]
                                            }, section.key))
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-4",
                                        children: [
                                            selected ? /*#__PURE__*/ _jsxs("section", {
                                                "data-testid": Testids.captureOutputs.sectionDetails,
                                                "data-section-key": selected.key,
                                                className: "space-y-3 rounded-md border p-4",
                                                children: [
                                                    /*#__PURE__*/ _jsx("h3", {
                                                        className: "text-lg font-semibold",
                                                        children: selected.title
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        children: readinessWhy(selected)
                                                    }),
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        children: [
                                                            "Source home: ",
                                                            selected.owner
                                                        ]
                                                    }),
                                                    (ownerDestination(selected) || !snapshotId) && /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "secondary",
                                                        "data-testid": Testids.captureOutputs.openSource,
                                                        onClick: ()=>openOwner(selected),
                                                        children: ownerDestination(selected) ? `Open Source · ${selected.owner}` : 'Open Outputs manual material'
                                                    }),
                                                    selected.sources.length === 0 && /*#__PURE__*/ _jsxs("p", {
                                                        children: [
                                                            "No source material yet.",
                                                            ' ',
                                                            selected.owner === 'Outputs manual material' ? 'Add a draft in Outputs manual material below.' : 'Open the owner to add source information, or supply manual fallback while no structured source exists.'
                                                        ]
                                                    }),
                                                    selected.sources.map((source, index)=>/*#__PURE__*/ _jsxs("details", {
                                                            open: true,
                                                            "data-testid": Testids.captureOutputs.sourceDetails,
                                                            className: "space-y-2 rounded-md border p-3",
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("summary", {
                                                                    className: "cursor-pointer font-medium",
                                                                    children: [
                                                                        source.kind === 'manual_material' ? 'Manual material' : selected.owner,
                                                                        " · source ",
                                                                        index + 1
                                                                    ]
                                                                }),
                                                                source.kind === 'black_hat_report' && /*#__PURE__*/ _jsx(SpecraButton, {
                                                                    variant: "secondary",
                                                                    onClick: ()=>update({
                                                                            outputTemplate: 'black-hat-report-v1',
                                                                            outputVersion: source.version,
                                                                            outputSnapshot: null,
                                                                            outputCompare: null,
                                                                            outputSection: null
                                                                        }),
                                                                    children: "Open referenced Black Hat report"
                                                                }),
                                                                /*#__PURE__*/ _jsx(SourceContent, {
                                                                    content: source.contentJson
                                                                }),
                                                                /*#__PURE__*/ _jsxs("details", {
                                                                    "data-testid": Testids.captureOutputs.sourceProvenance,
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("summary", {
                                                                            className: "cursor-pointer text-sm text-muted-foreground",
                                                                            children: "Source version and origin"
                                                                        }),
                                                                        /*#__PURE__*/ _jsxs("dl", {
                                                                            className: "break-all text-xs",
                                                                            children: [
                                                                                /*#__PURE__*/ _jsx("dt", {
                                                                                    children: "Source identity"
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("dd", {
                                                                                    children: source.id
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("dt", {
                                                                                    children: "Version"
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("dd", {
                                                                                    children: source.version
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("dt", {
                                                                                    children: "As of"
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("dd", {
                                                                                    children: dateLabel(source.asOf)
                                                                                })
                                                                            ]
                                                                        })
                                                                    ]
                                                                })
                                                            ]
                                                        }, `${source.id}-${source.version}`)),
                                                    snapshotId && selected.manualMaterial && /*#__PURE__*/ _jsxs("section", {
                                                        className: "space-y-1",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("h4", {
                                                                className: "font-semibold",
                                                                children: [
                                                                    "Saved supplementary manual material · revision",
                                                                    ' ',
                                                                    selected.manualMaterial.version
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "whitespace-pre-wrap",
                                                                children: selected.manualMaterial.narrative || 'Empty narrative'
                                                            }),
                                                            /*#__PURE__*/ _jsxs("p", {
                                                                className: "whitespace-pre-wrap",
                                                                children: [
                                                                    "Evidence: ",
                                                                    selected.manualMaterial.evidence || 'Not recorded'
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("p", {
                                                                className: "whitespace-pre-wrap",
                                                                children: [
                                                                    "Limitations: ",
                                                                    selected.manualMaterial.limitations || 'None recorded'
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("p", {
                                                                children: [
                                                                    "Internally reviewed: ",
                                                                    selected.manualMaterial.reviewed ? 'Yes' : 'No'
                                                                ]
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }) : /*#__PURE__*/ _jsx("p", {
                                                children: "The requested section is not in this template. Select an area from the list."
                                            }),
                                            !snapshotId && canWrite && query.data?.projection?.sections.map((section)=>/*#__PURE__*/ _jsx("div", {
                                                    "data-output-editor": section.key,
                                                    hidden: selected?.key !== section.key,
                                                    children: /*#__PURE__*/ _jsx(MaterialEditor, {
                                                        captureProjectId: captureProjectId,
                                                        sectionKey: section.key,
                                                        sessions: draftSessions.current,
                                                        head: section.manualMaterial,
                                                        history: (query.data?.materialHistory ?? []).filter((material)=>material.sectionKey === section.key),
                                                        onDenied: ()=>setRefused(true)
                                                    })
                                                }, section.key)),
                                            !snapshotId && !canWrite && /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm",
                                                children: "Manual edits and saving copies need permission to edit this pursuit."
                                            }),
                                            !snapshotId && !canWrite && selected?.manualMaterial && /*#__PURE__*/ _jsxs("section", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("h4", {
                                                        className: "font-semibold",
                                                        children: "Supplementary manual material"
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "whitespace-pre-wrap",
                                                        children: selected.manualMaterial.narrative
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "whitespace-pre-wrap",
                                                        children: selected.manualMaterial.evidence
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "whitespace-pre-wrap",
                                                        children: selected.manualMaterial.limitations
                                                    }),
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        children: [
                                                            "Internally reviewed: ",
                                                            selected.manualMaterial.reviewed ? 'Yes' : 'No'
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    !snapshotId && canWrite && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-3 border-t pt-4",
                        children: [
                            /*#__PURE__*/ _jsx("h4", {
                                className: "font-semibold",
                                children: "Save a dated review copy"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm",
                                children: "Purpose and template identify this review. Saving captures the latest authorized source basis on the server. Unsaved manual drafts are excluded."
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Purpose of this copy",
                                testId: Testids.captureOutputs.snapshotPurpose,
                                value: purpose,
                                onChange: setPurpose
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.captureOutputs.snapshotSave,
                                disabled: busy || !purpose.trim(),
                                onClick: saveSnapshot,
                                children: busy ? 'Saving copy…' : 'Save copy'
                            }),
                            saveError && /*#__PURE__*/ _jsx("p", {
                                role: "alert",
                                children: saveError
                            }),
                            saveMessage && /*#__PURE__*/ _jsx("output", {
                                children: saveMessage
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                "data-testid": Testids.captureOutputs.history,
                className: "space-y-3 rounded-md border p-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-lg font-semibold",
                        children: "History · saved review copies"
                    }),
                    history.isError ? /*#__PURE__*/ _jsxs("div", {
                        role: "alert",
                        children: [
                            "History could not be loaded.",
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>history.refetch(),
                                children: "Retry history"
                            })
                        ]
                    }) : !history.data?.snapshots.length ? /*#__PURE__*/ _jsx("p", {
                        children: "No saved copies yet. Partial live material can be saved for a dated review."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: history.data.snapshots.map((snapshot)=>/*#__PURE__*/ _jsxs("li", {
                                "data-snapshot-id": snapshot.id,
                                className: "rounded-md border p-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "font-medium",
                                        children: [
                                            snapshot.purpose,
                                            " · version ",
                                            snapshot.version
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            dateLabel(snapshot.createdAt),
                                            " ·",
                                            ' ',
                                            templateLabel(snapshot.projection?.templateId ?? '')
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            snapshot.projection?.readySections ?? 0,
                                            " of",
                                            ' ',
                                            snapshot.projection?.totalSections ?? 0,
                                            " areas ready at save"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        "data-testid": Testids.captureOutputs.snapshotOpen,
                                        onClick: ()=>update({
                                                outputSnapshot: snapshot.id,
                                                outputSection: null
                                            }),
                                        children: "Review saved copy"
                                    })
                                ]
                            }, snapshot.id))
                    })
                ]
            })
        ]
    });
}
